import { NOTIFICATION_FREQUENCIES, type NotificationFrequency } from '@clockwise/shared';
import { z } from 'zod';

export const NOTIFICATION_STATUSES = [
  'pending',
  'batched',
  'sent',
  'failed',
  'cancelled',
  'expired',
  'skipped',
  'read',
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

export const NOTIFICATION_CHANNELS = ['email', 'slack', 'gchat', 'sms', 'mobile_push'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NotificationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  userId: z.string().uuid(),
  templateName: z.string(),
  title: z.string(),
  body: z.string(),
  metadata: z.record(z.string(), z.unknown()),
  status: z.enum(NOTIFICATION_STATUSES),
  priority: z.enum(['critical', 'high', 'normal', 'low']),
  scheduledFor: z.string().nullable().optional(),
  sentAt: z.string().nullable().optional(),
  readAt: z.string().nullable().optional(),
  channel: z.enum(NOTIFICATION_CHANNELS).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const PreferenceViewSchema = z.object({
  templateName: z.string(),
  label: z.string().optional(),
  description: z.string().optional(),
  category: z.string().optional(),
  /** `'window'` when the template's frequency is meaningful (digestable). */
  batchMode: z.enum(['window']).optional(),
  enabled: z.boolean(),
  channels: z.array(z.enum(NOTIFICATION_CHANNELS)),
  frequency: z.enum(NOTIFICATION_FREQUENCIES),
  source: z.enum(['default', 'override']),
});
export type PreferenceView = z.infer<typeof PreferenceViewSchema>;

export const TemplateDefinitionSchema = z.object({
  name: z.string(),
  label: z.string(),
  description: z.string(),
  category: z.string(),
  defaultEnabled: z.boolean(),
  defaultFrequency: z.enum(NOTIFICATION_FREQUENCIES),
  defaultChannels: z.array(z.enum(NOTIFICATION_CHANNELS)),
  isBatchTemplate: z.boolean(),
});
export type TemplateDefinition = z.infer<typeof TemplateDefinitionSchema>;

export interface ListNotificationsQuery {
  status?: NotificationStatus;
  unreadOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface UpdatePreferenceInput {
  enabled?: boolean;
  channels?: NotificationChannel[];
  frequency?: NotificationFrequency;
  payload?: Record<string, unknown> | null;
}
